import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  crmApi,
  type ContactQuery,
  type CreateContactPayload,
  type CreateOpportunityPayload,
  type UpdateContactPayload,
  type PipelineQuery,
  type UpdateOpportunityPayload,
} from '@/api/crm';

const CONTACTS_KEY = ['crm', 'contacts'] as const;
const PIPELINE_KEY = ['crm', 'pipeline'] as const;

// ── CONTACTS ───────────────────────────────────────────────

export const useContacts = (query: ContactQuery = {}) =>
  useQuery({
    queryKey:  [...CONTACTS_KEY, query],
    queryFn:   () => crmApi.getContacts(query),
    staleTime: 2 * 60_000,
  });

export const useCreateContact = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateContactPayload) => crmApi.createContact(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: CONTACTS_KEY }),
  });
};

/** TICKET-004 : modifier une fiche contact (rafraîchit aussi les factures, qui affichent le client). */
export const useUpdateContact = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateContactPayload }) => crmApi.updateContact(id, payload),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: CONTACTS_KEY }); void qc.invalidateQueries({ queryKey: ['finance'] }); },
  });
};

/** Une fiche contact par son identifiant (lien direct depuis une erreur de facture). */
export const useContact = (id: string | undefined) =>
  useQuery({ queryKey: [...CONTACTS_KEY, 'fiche', id], queryFn: () => crmApi.getContact(id!), enabled: Boolean(id) });

// ── PIPELINE ───────────────────────────────────────────────

export const usePipeline = (query: PipelineQuery = {}) =>
  useQuery({
    queryKey:  [...PIPELINE_KEY, query],
    queryFn:   () => crmApi.getPipeline(query),
    staleTime: 30_000,
  });

export const useCreateOpportunity = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateOpportunityPayload) => crmApi.createOpportunity(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: PIPELINE_KEY }),
  });
};

export const useUpdateOpportunity = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateOpportunityPayload }) =>
      crmApi.updateOpportunity(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: PIPELINE_KEY }),
  });
};
